import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDarkClasses } from '../../hooks/useDarkClasses';

/**
 * Shared tooltip primitive, generalised from the portal-based implementation
 * that was previously private to ContentAccordion. Rendering into a portal
 * keeps it clear of `overflow-hidden` card boundaries, and it flips to the left
 * when there isn't room on the right.
 *
 * Supports BOTH hover (desktop) and tap (mobile) — a tap-only or hover-only
 * tooltip would strand one set of users.
 */
interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  /** Accessible label for the trigger when the child has no text of its own. */
  label?: string;
  maxWidth?: number;
}
export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  label,
  maxWidth = 300
}) => {
  const dk = useDarkClasses();
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0
  });
  const [flipLeft, setFlipLeft] = useState(false);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const position = useCallback(() => {
    const node = triggerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const spaceOnRight = window.innerWidth - rect.left;
    if (spaceOnRight < maxWidth + 32) {
      setCoords({
        top: rect.top - 8,
        left: rect.right
      });
      setFlipLeft(true);
    } else {
      setCoords({
        top: rect.top - 8,
        left: rect.left
      });
      setFlipLeft(false);
    }
  }, [maxWidth]);
  const show = useCallback(() => {
    position();
    setVisible(true);
  }, [position]);
  const hide = useCallback(() => setVisible(false), []);

  // Dismiss on outside tap, scroll, or Escape so a tapped tooltip on mobile
  // never becomes stuck to the screen.
  useEffect(() => {
    if (!visible) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) hide();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', hide, true);
    };
  }, [visible, hide]);
  return <>
      <span ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} onClick={event => {
      event.stopPropagation();
      event.preventDefault();
      if (visible) hide();else show();
    }} tabIndex={0} role="button" aria-label={label} aria-expanded={visible} className="inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-current rounded-full" data-id="element-3902" data-mp="1zyrj8ww71yxw">
        {children}
      </span>
      {visible && ReactDOM.createPortal(<div role="tooltip" style={{
      position: 'fixed',
      top: coords.top,
      left: coords.left,
      transform: flipLeft ? 'translate(-100%, -100%)' : 'translateY(-100%)',
      zIndex: 9999,
      maxWidth,
      width: 'max-content'
    }} className={`text-xs leading-relaxed rounded-lg px-3 py-2 shadow-lg pointer-events-none ${dk.isDark ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-white text-gray-700 border border-gray-200'}`} data-id="element-3903" data-mp="1but958mngcdc">
            {content}
          </div>, document.body)}
    </>;
};
export default Tooltip;